import Dexie, { type EntityTable } from 'dexie'
import type {
  Ajustes,
  Bitacora,
  Cliente,
  Devolucion,
  Gasto,
  Lote,
  Movimiento,
  Producto,
  Usuario,
  Venta,
} from '../types'
import { AJUSTES_POR_DEFECTO, type Adapter, type Snapshot, type Tabla } from './adapter'

class NovaBerryDB extends Dexie {
  usuarios!: EntityTable<Usuario, 'id'>
  productos!: EntityTable<Producto, 'id'>
  lotes!: EntityTable<Lote, 'id'>
  movimientos!: EntityTable<Movimiento, 'id'>
  clientes!: EntityTable<Cliente, 'id'>
  ventas!: EntityTable<Venta, 'id'>
  devoluciones!: EntityTable<Devolucion, 'id'>
  bitacora!: EntityTable<Bitacora, 'id'>
  gastos!: EntityTable<Gasto, 'id'>
  ajustes!: EntityTable<Ajustes, 'id'>

  constructor() {
    super('novaberry')
    this.version(1).stores({
      usuarios: 'id, email, rol',
      productos: 'id, codigo, categoria, nombre, codigoBarras',
      lotes: 'id, productoId, vencimiento',
      movimientos: 'id, productoId, fecha, tipo',
      clientes: 'id, tipo, nombre, rut',
      ventas: 'id, folio, clienteId, usuarioId, fecha, estado',
      devoluciones: 'id, ventaId, fecha',
      bitacora: 'id, usuarioId, fecha',
      ajustes: 'id',
    })
    // v2: gastos de la empresa (arriendo, publicidad, compras, etc.).
    this.version(2).stores({
      gastos: 'id, categoria, tipo, fecha',
    })
  }
}

export const db = new NovaBerryDB()

export const localAdapter: Adapter = {
  nombre: 'local',

  async disponible() {
    return typeof indexedDB !== 'undefined'
  },

  async cargar(): Promise<Snapshot> {
    const [
      usuarios,
      productos,
      lotes,
      movimientos,
      clientes,
      ventas,
      devoluciones,
      bitacora,
      gastos,
      ajustes,
    ] = await Promise.all([
      db.usuarios.toArray(),
      db.productos.toArray(),
      db.lotes.toArray(),
      db.movimientos.toArray(),
      db.clientes.toArray(),
      db.ventas.toArray(),
      db.devoluciones.toArray(),
      db.bitacora.toArray(),
      db.gastos.toArray(),
      db.ajustes.get('ajustes'),
    ])
    return {
      usuarios,
      productos,
      lotes,
      movimientos,
      clientes,
      ventas,
      devoluciones,
      bitacora,
      gastos,
      ajustes: { ...AJUSTES_POR_DEFECTO, ...ajustes },
    }
  },

  async guardar(tabla, filas) {
    if (!filas.length) return
    await (db[tabla] as EntityTable<{ id: string }, 'id'>).bulkPut(filas as { id: string }[])
  },

  async eliminar(tabla: Tabla, ids: string[]) {
    if (!ids.length) return
    await (db[tabla] as EntityTable<{ id: string }, 'id'>).bulkDelete(ids)
  },

  async guardarAjustes(ajustes: Ajustes) {
    await db.ajustes.put(ajustes)
  },

  async reemplazarTodo(s: Snapshot) {
    await db.transaction(
      'rw',
      [
        db.usuarios,
        db.productos,
        db.lotes,
        db.movimientos,
        db.clientes,
        db.ventas,
        db.devoluciones,
        db.bitacora,
        db.gastos,
        db.ajustes,
      ],
      async () => {
        await Promise.all([
          db.usuarios.clear(),
          db.productos.clear(),
          db.lotes.clear(),
          db.movimientos.clear(),
          db.clientes.clear(),
          db.ventas.clear(),
          db.devoluciones.clear(),
          db.bitacora.clear(),
          db.gastos.clear(),
          db.ajustes.clear(),
        ])
        await Promise.all([
          db.usuarios.bulkPut(s.usuarios),
          db.productos.bulkPut(s.productos),
          db.lotes.bulkPut(s.lotes),
          db.movimientos.bulkPut(s.movimientos),
          db.clientes.bulkPut(s.clientes),
          db.ventas.bulkPut(s.ventas),
          db.devoluciones.bulkPut(s.devoluciones),
          db.bitacora.bulkPut(s.bitacora),
          db.gastos.bulkPut(s.gastos),
          db.ajustes.put(s.ajustes),
        ])
      },
    )
  },
}
